import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { EditIcon, TrashIcon } from './ActionIcons';
import ConfirmDialog from './ConfirmDialog';
import Modal from './Modal';

const BRAND_GREEN = '#1C3D2E';
const GRAY_TEXT = '#5E6561';
const BORDER = '#EBEDEC';
const CARD_BG = '#FFFFFF';

/**
 * Категории каталога — две колонки (Стебли | Букеты).
 * Порядок отображения «снизу вверх» — sort_order=1 ВНИЗУ таблицы (первая на витрине).
 * Поддерживает drag-and-drop: перетащил строку → backend перенумеровывает 1..N последовательно.
 */
export default function CategoriesTab({ showToast }) {
  const [categories, setCategories] = useState([]);
  const [productsCount, setProductsCount] = useState({});
  const [bouquetsCount, setBouquetsCount] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | {scope} | category
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      const cats = await api.getCategories();
      setCategories(cats || []);
      setIsLoading(false);

      api.getProducts(1, 500, false).then(resp => {
        const pBy = {};
        (resp.items || resp || []).forEach(p => {
          const cid = p.category?.id;
          if (cid) pBy[cid] = (pBy[cid] || 0) + 1;
        });
        setProductsCount(pBy);
      }).catch(() => {});

      api.getBouquetsAdmin(false).then(resp => {
        const bBy = {};
        (resp.items || []).forEach(b => {
          const cid = b.category_id;
          if (cid) bBy[cid] = (bBy[cid] || 0) + 1;
        });
        setBouquetsCount(bBy);
      }).catch(() => {});
    } catch (e) {
      showToast?.('Ошибка загрузки категорий', 'error');
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteCategory(confirmDelete.id);
      showToast?.('Категория удалена', 'success');
      setConfirmDelete(null);
      load();
    } catch (e) {
      showToast?.(e.message || 'Не удалось удалить', 'error');
    }
  };

  // Display порядок = ASC (сверху №1). Backend тоже ждёт ASC — передаём как есть.
  const handleReorder = async (scope, displayOrderIds) => {
    setCategories(prev => prev.map(c => {
      const idx = displayOrderIds.indexOf(c.id);
      return idx >= 0 ? { ...c, sort_order: idx + 1 } : c;
    }));
    try {
      await api.reorderCategories(scope, displayOrderIds);
    } catch (e) {
      showToast?.(e.message || 'Не удалось сохранить порядок', 'error');
      load();
    }
  };

  if (isLoading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  // ASC по sort_order — №1 наверху таблицы, №N внизу.
  const stems = categories.filter(c => c.scope === 'stems' || !c.scope).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const bouquets = categories.filter(c => c.scope === 'bouquets').sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setEditing({ isNew: true, scope: 'stems' })}
          className="btn btn-primary btn-md"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Добавить категорию
        </button>
      </div>

      {/* На телефоне — одна колонка: в две по 160 px кнопки ✎/🗑 уезжали за край */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 24,
      }}>
        <CategoryColumn
          title="Категории стеблей"
          scope="stems"
          items={stems}
          countMap={productsCount}
          countLabel="Стеблей"
          onEdit={(c) => setEditing(c)}
          onDelete={(c) => setConfirmDelete(c)}
          onReorder={(ids) => handleReorder('stems', ids)}
        />
        <CategoryColumn
          title="Категории букетов"
          scope="bouquets"
          items={bouquets}
          countMap={bouquetsCount}
          countLabel="Букетов"
          onEdit={(c) => setEditing(c)}
          onDelete={(c) => setConfirmDelete(c)}
          onReorder={(ids) => handleReorder('bouquets', ids)}
        />
      </div>

      {editing && (
        <CategoryEditModal
          category={editing.isNew ? null : editing}
          defaultScope={editing.scope || 'stems'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить категорию?"
          message={`Категория «${confirmDelete.name}» будет удалена. Если в ней есть товары или букеты — операция не пройдёт.`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
          variant="danger"
        />
      )}
    </div>
  );
}


function CategoryColumn({ title, scope, items, countMap, countLabel, onEdit, onDelete, onReorder }) {
  // Локальный порядок (для optimistic UI во время drag)
  const [order, setOrder] = useState(items);
  // Каждый раз когда внешний items меняется (после load) — синхронизируем
  useEffect(() => { setOrder(items); }, [items]);

  const dragIndexRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const handleDragStart = (e, idx) => {
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    // Чтобы Firefox разрешил drag, нужно установить data
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverIndex !== idx) setHoverIndex(idx);
  };

  const handleDragLeave = () => setHoverIndex(null);

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    setHoverIndex(null);
    dragIndexRef.current = null;
    if (from == null || from === dropIdx) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    setOrder(next);
    onReorder(next.map(c => c.id));  // в display order (DESC по sort_order)
  };

  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, '--ch': '8px', '--stroke': (BORDER), overflow: 'hidden' }}>
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${BORDER}`,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: BRAND_GREEN }}>
          {title}
        </h3>
      </div>

      {order.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: GRAY_TEXT, fontSize: 13 }}>
          Категорий нет
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#FAFAF7' }}>
              <th style={{ ...th, width: 30 }}></th>
              <th style={th}>Название</th>
              <th style={{ ...th, textAlign: 'center', width: 80 }}>{countLabel}</th>
              <th style={{ ...th, textAlign: 'center', width: 70 }}>№</th>
              <th style={{ ...th, width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {order.map((c, idx) => {
              const count = countMap[c.id] || 0;
              const isActive = c.is_active !== false;
              const isHovered = hoverIndex === idx;
              return (
                <tr
                  key={c.id}
                  draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, idx)}
                  style={{
                    borderTop: `1px solid ${BORDER}`,
                    opacity: isActive ? 1 : 0.55,
                    background: isHovered ? '#EFFAF4' : 'transparent',
                    cursor: 'grab',
                  }}
                >
                  <td style={{ ...td, color: '#bbb', textAlign: 'center', userSelect: 'none' }} title="Перетащите для изменения порядка">⋮⋮</td>
                  <td style={td}>
                    <span style={{ fontWeight: 500 }}>{c.name}</span>
                    {!isActive && <span style={{ fontSize: 11, marginLeft: 6, color: GRAY_TEXT }}>(скрыта)</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{count || '—'}</td>
                  <td style={{ ...td, textAlign: 'center', color: GRAY_TEXT, fontWeight: 500 }}>
                    {c.sort_order || 0}
                  </td>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} draggable={false}>
                      <IconBtn onClick={() => onEdit(c)} title="Редактировать"><EditIcon /></IconBtn>
                      <IconBtn
                        onClick={() => onDelete(c)}
                        disabled={count > 0}
                        title={count > 0 ? `В категории ${count} элементов` : 'Удалить'}
                        variant="danger"
                      ><TrashIcon /></IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


function CategoryEditModal({ category, defaultScope, onClose, onSaved, showToast }) {
  const isNew = !category;
  const [name, setName] = useState(category?.name || '');
  const [scope, setScope] = useState(category?.scope || defaultScope || 'stems');
  const [isActive, setIsActive] = useState(category?.is_active !== false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { showToast?.('Введите название', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        scope,
        is_active: isActive,
        // sort_order не передаём — backend поставит max+1 для новой; для существующей не трогаем
      };
      if (isNew) {
        await api.createCategory(payload);
        showToast?.('Категория создана', 'success');
      } else {
        await api.updateCategory(category.id, payload);
        showToast?.('Категория обновлена', 'success');
      }
      onSaved();
    } catch (e) {
      showToast?.(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} confirmOverlayClose title={isNew ? 'Новая категория' : `Редактирование: ${category.name}`}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <Label>Раздел каталога</Label>
          <div className="seg" style={{ display: 'flex' }}>
            <ScopeBtn active={scope === 'stems'} onClick={() => setScope('stems')} disabled={!isNew}>Стебли</ScopeBtn>
            <ScopeBtn active={scope === 'bouquets'} onClick={() => setScope('bouquets')} disabled={!isNew}>Букеты</ScopeBtn>
          </div>
          {!isNew && (
            <small style={{ color: GRAY_TEXT, fontSize: 11 }}>Раздел нельзя менять после создания.</small>
          )}
        </div>

        <div>
          <Label>Название</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Например, «Розы»" />
        </div>

        <div style={{ background: '#EFFAF4', border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), padding: '10px 12px', fontSize: 12, color: GRAY_TEXT }}>
          Порядок устанавливается перетаскиванием в таблице. Новая категория добавится в конец.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          Активна
        </label>

        {/* Подвал: [Отмена] [Сохранить] — общие .btn (UX-аудит 25.10) */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохраняем...' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
        </div>
      </div>
    </Modal>
  );
}


// Действие в строке — общий .btn-icon, как в Сотрудниках и Настройках (UX-аудит 25.10)
function IconBtn({ children, onClick, variant, disabled, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
      className={`btn-icon${variant === 'danger' ? ' is-danger' : ''}`}
      style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}>{children}</button>
  );
}

// Раздел каталога — переключатель «одно из», общий .seg-btn (UX-аудит 25.10)
function ScopeBtn({ active, onClick, disabled, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`seg-btn${active ? ' on' : ''}`}
      style={{
        flex: 1,
        ...(disabled ? { cursor: 'not-allowed', opacity: active ? 1 : 0.5 } : null),
      }}>{children}</button>
  );
}

const th = {
  padding: '10px 12px', textAlign: 'left',
  fontSize: 12, fontWeight: 600, color: GRAY_TEXT,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const td = { padding: '10px 12px', verticalAlign: 'middle' };

function Label({ children }) {
  return <div style={{ fontSize: 12, color: GRAY_TEXT, marginBottom: 4, fontWeight: 600 }}>{children}</div>;
}
function Input({ ...props }) {
  return <input {...props} style={{
    width: '100%', padding: 8, border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': (BORDER),
    fontSize: 14, boxSizing: 'border-box', ...(props.style || {}),
  }} />;
}
